import { Component, OnInit } from '@angular/core';
import { MenuController, NavController, Platform } from '@ionic/angular';
import { StatusBar } from '@capacitor/status-bar';
import { MapService } from './services/map.service';
import { TranslateService } from '@ngx-translate/core';
import { Account, LoopBackAuth, LoopBackConfig } from '../Api';
import { environment } from '../environments/environment';
import { Router, NavigationEnd } from '@angular/router';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html'
})
export class AppComponent implements OnInit {
    authAccount = new Account();
    isFullscreen = false;
    isHomePage = true;

    btnX = Math.max(10, window.innerWidth - 65);
    btnY = 15;
    private isDragging = false;
    private startX = 0;
    private startY = 0;
    private initialBtnX = 0;
    private initialBtnY = 0;
    private hasMoved = false;

    constructor(
        private platform: Platform,
        public mapService: MapService,
        private translateService: TranslateService,
        public router: Router,
        private navCtr: NavController,
        private auth: LoopBackAuth) {
            this.router.events.pipe(
                filter(event => event instanceof NavigationEnd)
            ).subscribe((event: NavigationEnd) => {
                const url = event.urlAfterRedirects || event.url;
                this.isHomePage = url === '/' || url === '/home' || url.includes('home');
            });
        }

    async ngOnInit() {
        try {
            LoopBackConfig.setBaseURL(environment.apiUrl);
            this.detectMobile();
            this.mapService.setupAutoplayUnlocker();
            this.setupFullscreenListeners();
            await this.initializeApp();
            try {
                this.authAccount = this.auth.getCurrentUserData();
            } catch (e) {
                console.log('getCurrentUserData error', e);
            }
            this.translateService.addLangs(['vi', 'en']);
            try {
                const val = await this.initLocalize();
                if (val) {
                    await this.mapService.saveSetting(val);
                }
            } catch (e) {
                console.log('initLocalize error', e);
                this.translateService.setDefaultLang('vi');
                this.translateService.use('vi');
            }
        } catch (e) {
            console.log('Init app error', e);
        } finally {
            console.log('AppComponent init complete, navigating to home');
            await this.navCtr.navigateRoot('home');
        }
    }

    detectMobile() {
        const isIPhone = this.platform.is('iphone') || (typeof navigator !== 'undefined' && navigator.userAgent && /iPhone|iPod/.test(navigator.userAgent));
        const isAndroid = this.platform.is('android') || (typeof navigator !== 'undefined' && navigator.userAgent && /Android/i.test(navigator.userAgent));
        const isMobile = isIPhone || isAndroid || this.platform.is('mobileweb') || this.platform.is('cordova') || this.platform.is('capacitor') || (typeof window !== 'undefined' && (window.innerWidth <= 950 && window.innerHeight <= 600 || window.innerHeight <= 950 && window.innerWidth <= 600));

        if (isIPhone) {
            document.documentElement.classList.add('is-iphone');
            document.body.classList.add('is-iphone');
        }
        if (isAndroid) {
            document.documentElement.classList.add('is-android');
            document.body.classList.add('is-android');
        }
        if (isMobile) {
            document.documentElement.classList.add('is-mobile');
            document.body.classList.add('is-mobile');
        }
    }

    setupFullscreenListeners() {
        const events = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'];
        events.forEach(evt => {
            document.addEventListener(evt, () => {
                const doc: any = document;
                const isFS = !!(doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement || doc.msFullscreenElement);
                this.isFullscreen = isFS;
                if (isFS) {
                    document.documentElement.classList.add('app-is-fullscreen');
                } else {
                    document.documentElement.classList.remove('app-is-fullscreen');
                }
            });
        });
    }

    toggleFullscreen() {
        const doc: any = document;
        const docEl: any = document.documentElement;

        const requestFS = docEl.requestFullscreen || docEl.webkitRequestFullscreen || docEl.mozRequestFullScreen || docEl.msRequestFullscreen;
        const exitFS = doc.exitFullscreen || doc.webkitExitFullscreen || doc.mozCancelFullScreen || doc.msExitFullscreen;
        const currentFS = doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement || doc.msFullscreenElement;

        if (!currentFS && !this.isFullscreen) {
            if (requestFS) {
                try {
                    const res = requestFS.call(docEl);
                    if (res && res.then) {
                        res.then(() => {
                            this.isFullscreen = true;
                        }).catch(() => {
                            this.fallbackIOSFullscreen();
                        });
                    } else {
                        this.isFullscreen = true;
                    }
                } catch (e) {
                    this.fallbackIOSFullscreen();
                }
            } else {
                this.fallbackIOSFullscreen();
            }
        } else {
            if (exitFS && currentFS) {
                try {
                    const res = exitFS.call(doc);
                    if (res && res.then) {
                        res.then(() => {
                            this.isFullscreen = false;
                        }).catch(() => {
                            this.fallbackIOSFullscreenExit();
                        });
                    } else {
                        this.isFullscreen = false;
                    }
                } catch (e) {
                    this.fallbackIOSFullscreenExit();
                }
            } else {
                this.fallbackIOSFullscreenExit();
            }
        }
    }

    fallbackIOSFullscreen() {
        this.isFullscreen = true;
        document.documentElement.classList.add('ios-fullscreen');
        document.body.classList.add('ios-fullscreen');
    }

    fallbackIOSFullscreenExit() {
        this.isFullscreen = false;
        document.documentElement.classList.remove('ios-fullscreen');
        document.body.classList.remove('ios-fullscreen');
    }

    onDragStart(event: any) {
        this.isDragging = true;
        this.hasMoved = false;
        const clientX = event.touches ? event.touches[0].clientX : event.clientX;
        const clientY = event.touches ? event.touches[0].clientY : event.clientY;
        this.startX = clientX;
        this.startY = clientY;
        this.initialBtnX = this.btnX;
        this.initialBtnY = this.btnY;

        if (!event.touches) {
            const onMouseMove = (e: MouseEvent) => this.onDragMove(e);
            const onMouseUp = (e: MouseEvent) => {
                this.onDragEnd(e);
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);
            };
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
        }
    }

    onDragMove(event: any) {
        if (!this.isDragging) {
            return;
        }
        const clientX = event.touches ? event.touches[0].clientX : event.clientX;
        const clientY = event.touches ? event.touches[0].clientY : event.clientY;
        const dx = clientX - this.startX;
        const dy = clientY - this.startY;

        if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
            this.hasMoved = true;
        }

        if (this.hasMoved) {
            if (event.cancelable) {
                event.preventDefault();
            }
            const newX = this.initialBtnX + dx;
            const newY = this.initialBtnY + dy;
            const maxWidth = window.innerWidth - 50;
            const maxHeight = window.innerHeight - 50;
            this.btnX = Math.max(10, Math.min(maxWidth, newX));
            this.btnY = Math.max(10, Math.min(maxHeight, newY));
        }
    }

    onDragEnd(event: any) {
        if (!this.isDragging) {
            return;
        }
        this.isDragging = false;
        if (!this.hasMoved) {
            this.toggleFullscreen();
        }
    }

    async initializeApp() {
        try {
            console.log('Init platform start');
            const readySource = await this.platform.ready();
            console.log('Init platform finish, source is:', readySource);
            if (readySource === 'dom') {
                console.log('Platform is Desktop: ',this.platform.platforms());
                this.mapService.setPlatformIsDesktop(true);
            } else {
                console.log('Platform is Mobile: ',this.platform.platforms());
                this.mapService.setPlatformIsDesktop(false);
                if (!this.platform.is('ios')) {
                    StatusBar.setOverlaysWebView({overlay: false}).then().catch(e => console.log('Status bar setOverlaysWebView hide error', e));
                    StatusBar.hide().then().catch(e => console.log('StatusBar hide error', e));
                    fromEvent(document.getElementsByTagName('body').item(0), 'click').subscribe({
                        next: async () => {
                            const {visible} = await StatusBar.getInfo();
                            if (visible) {
                                StatusBar.hide().then().catch(e => console.log('StatusBar hide error', e));
                            }
                        },
                    });
                }
                this.mapService.initDeviceId();
            }
            // this.platform.backButton.subscribe(() => {
            //     navigator['app'].exitApp();
            // });
            this.platform.pause.subscribe(() => {
                this.mapService.stopMusic();
            });
            this.platform.resume.subscribe(() => {
                this.mapService.resumeMusic();
            });
        } catch (e) {
            console.log('Init platform error', e);
        }
        this.mapService.initMapsProperties(null);
    }

    async initLocalize() {
        const val: any = await this.mapService.getSetting();
        if (val && val.language) {
            if (val.language === 'en') {
            } else {
                val.language = 'vi';
            }
            this.mapService.useSetting(val);
            this.translateService.setDefaultLang(val.language);
            this.translateService.use(val.language);
        } else {
            this.translateService.setDefaultLang('vi');
            let browserLang = this.translateService.getBrowserLang();
            if (browserLang !== 'en') {
                browserLang = 'vi';
            }
            this.translateService.use(browserLang);
        }
        return val;
    }
}
